import { z } from 'zod';
import { defineTool, STAFF, CLINICAL_STAFF, EVERYONE } from './define.js';
import { IsoDate } from '../types/common.js';
import { PatientBannerSchema, PatientRegistrationInputSchema, PatientSchema } from '../types/patient.js';
import { PendingActionSchema } from '../types/session.js';
import { CdsFindingSchema } from '../cds.js';
import { AllergySchema, HistoryEntrySchema, ImmunizationSchema, ProblemSchema, VitalSignsSchema, DiagnosisSchema } from '../types/clinical.js';

export const PatientMatchSchema = PatientBannerSchema.extend({
  /** 0..1 similarity on the search terms; identity still needs MRN/DOB or user confirmation when >1 match. */
  score: z.number().min(0).max(1),
  matchedOn: z.array(z.enum(['MRN', 'NAME', 'BIRTH_DATE', 'NATIONAL_ID', 'PHONE'])),
});

export const searchPatients = defineTool({
  name: 'searchPatients',
  description: 'Search the tenant patient index by name (Arabic or Latin), MRN, national id, phone or birth date. Returns candidates only; it never selects a patient.',
  input: z.object({
    query: z.string().min(1).max(120).describe('Free text: name fragment, MRN, national id or phone'),
    birthDate: IsoDate.optional(),
    limit: z.number().int().min(1).max(20).default(10),
  }),
  output: z.object({ matches: z.array(PatientMatchSchema) }),
  roles: STAFF, requiresPatientContext: false, write: false, exposeToLlm: true, audit: 'PATIENT_SEARCH',
});

export const getPatient = defineTool({
  name: 'getPatient',
  description: 'Fetch full demographics for a patient by id. Requires the patient to be the active context or an explicit, verified patientId.',
  input: z.object({ patientId: z.string().optional().describe('Defaults to the active patient') }),
  output: z.object({ patient: PatientSchema }),
  roles: STAFF, requiresPatientContext: false, write: false, exposeToLlm: true, audit: 'PATIENT_VIEW',
});

export const setPatientContext = defineTool({
  name: 'setPatientContext',
  description: 'Make a patient the active context for this session. Provide patientId from searchPatients when unambiguous, or identifiers (MRN and/or birth date) to verify identity. If more than one patient matches, the tool returns candidates and does NOT change context.',
  input: z.object({
    patientId: z.string().optional(),
    mrn: z.string().optional(),
    name: z.string().optional(),
    birthDate: IsoDate.optional(),
    /** The user explicitly confirmed a candidate offered in a clarification. */
    userConfirmed: z.boolean().default(false),
  }),
  output: z.object({
    status: z.enum(['SET', 'AMBIGUOUS', 'NOT_FOUND', 'CONFIRMATION_REQUIRED']),
    patient: PatientBannerSchema.optional(),
    candidates: z.array(PatientMatchSchema).default([]),
    invalidatedPendingActions: z.number().int().default(0),
    activeEncounterId: z.string().nullable().default(null),
  }),
  roles: STAFF, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'CONTEXT_SET',
});

export const clearPatientContext = defineTool({
  name: 'clearPatientContext',
  description: 'Clear the active patient context and invalidate pending actions for that patient.',
  input: z.object({}),
  output: z.object({ cleared: z.boolean(), invalidatedPendingActions: z.number().int() }),
  roles: STAFF, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'CONTEXT_CLEAR',
});

export const getPatientContext = defineTool({
  name: 'getPatientContext',
  description: 'Return the currently active patient and encounter for this session, or null.',
  input: z.object({}),
  output: z.object({ patient: PatientBannerSchema.nullable(), activeEncounterId: z.string().nullable(), setAt: z.string().optional() }),
  roles: STAFF, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const RegistrationFieldStatusSchema = z.object({
  collected: PatientRegistrationInputSchema.deepPartial(),
  missing: z.array(z.string()).describe('Dot paths of required fields not yet provided'),
  possibleDuplicates: z.array(PatientMatchSchema),
});

export const createRegistrationDraft = defineTool({
  name: 'createRegistrationDraft',
  description: 'Start or update a new-patient registration. Pass whatever demographics are known; the tool reports missing required fields and possible duplicates. When nothing is missing it returns a pending action to confirm.',
  input: z.object({
    draftId: z.string().optional().describe('Omit to start a new draft'),
    data: PatientRegistrationInputSchema.deepPartial(),
  }),
  output: z.object({
    draftId: z.string(),
    status: z.enum(['INCOMPLETE', 'READY']),
    fields: RegistrationFieldStatusSchema,
    pendingAction: PendingActionSchema.optional(),
  }),
  roles: STAFF, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'PATIENT_REGISTER',
});

export const updatePatientDemographicsDraft = defineTool({
  name: 'updatePatientDemographicsDraft',
  description: 'Prepare a demographics/contact update for the active patient. Returns a pending action for confirmation.',
  input: z.object({ changes: PatientRegistrationInputSchema.deepPartial() }),
  output: z.object({ pendingAction: PendingActionSchema }),
  roles: STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'PATIENT_UPDATE',
});

const SummaryItem = z.object({ label: z.string(), value: z.string(), date: z.string().optional(), flag: z.string().optional(), id: z.string().optional() });

export const getPatientSummary = defineTool({
  name: 'getPatientSummary',
  description: 'Structured longitudinal summary of the active patient: active problems, allergies, current medications, recent vitals, latest abnormal labs, last encounters, upcoming appointments. All items are verified record data.',
  input: z.object({ patientId: z.string().optional() }),
  output: z.object({
    patient: PatientBannerSchema,
    age: z.number().int(),
    problems: z.array(SummaryItem),
    allergies: z.array(SummaryItem),
    medications: z.array(SummaryItem),
    recentVitals: z.array(SummaryItem),
    abnormalLabs: z.array(SummaryItem),
    recentEncounters: z.array(SummaryItem),
    upcomingAppointments: z.array(SummaryItem),
    /** Explicitly lists sections with no data so the LLM reports "no recorded X" instead of guessing. */
    emptySections: z.array(z.string()),
  }),
  roles: [...STAFF, 'PATIENT'], requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const getMedicalHistory = defineTool({
  name: 'getMedicalHistory',
  description: 'Medical, surgical, family and social history plus problems, diagnoses, allergies and immunizations for the active patient.',
  input: z.object({ categories: z.array(z.enum(['MEDICAL', 'SURGICAL', 'FAMILY', 'SOCIAL', 'PROBLEMS', 'DIAGNOSES', 'ALLERGIES', 'IMMUNIZATIONS'])).optional() }),
  output: z.object({
    history: z.array(HistoryEntrySchema),
    problems: z.array(ProblemSchema),
    diagnoses: z.array(DiagnosisSchema),
    allergies: z.array(AllergySchema),
    immunizations: z.array(ImmunizationSchema),
  }),
  roles: [...STAFF, 'PATIENT'], requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const getVitals = defineTool({
  name: 'getVitals',
  description: 'Vital sign measurements for the active patient, newest first.',
  input: z.object({ since: IsoDate.optional(), limit: z.number().int().min(1).max(100).default(20) }),
  output: z.object({ vitals: z.array(VitalSignsSchema) }),
  roles: [...STAFF, 'PATIENT'], requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const RecordEntryDraftSchema = z.discriminatedUnion('entryType', [
  z.object({ entryType: z.literal('ALLERGY'), data: AllergySchema.pick({ substance: true, category: true, reaction: true, severity: true }).partial({ reaction: true, severity: true }) }),
  z.object({ entryType: z.literal('PROBLEM'), data: ProblemSchema.pick({ description: true, onsetDate: true, severity: true, code: true }).partial({ onsetDate: true, severity: true, code: true }) }),
  z.object({ entryType: z.literal('HISTORY'), data: HistoryEntrySchema.pick({ category: true, description: true, date: true, qualifier: true }).partial({ date: true, qualifier: true }) }),
  z.object({ entryType: z.literal('IMMUNIZATION'), data: ImmunizationSchema.pick({ vaccine: true, date: true, doseNumber: true }).partial({ doseNumber: true }) }),
  z.object({ entryType: z.literal('VITALS'), data: VitalSignsSchema.pick({ systolicMmHg: true, diastolicMmHg: true, heartRateBpm: true, respiratoryRate: true, temperatureC: true, spo2Percent: true, weightKg: true, heightCm: true, painScore: true }) }),
  z.object({ entryType: z.literal('DIAGNOSIS'), data: DiagnosisSchema.pick({ code: true, description: true, rank: true }).partial({ rank: true }) }),
]);

export const createRecordEntryDraft = defineTool({
  name: 'createRecordEntryDraft',
  description: 'Prepare a structured record entry (allergy, problem, history item, immunization, vital signs, diagnosis) for the active patient from an explicit clinician statement. Returns a pending action; nothing is saved until confirmed.',
  input: z.object({
    entry: RecordEntryDraftSchema,
    provenance: z.enum(['clinician-entered', 'patient-reported']).default('clinician-entered'),
  }),
  output: z.object({ pendingAction: PendingActionSchema, cdsFindings: z.array(CdsFindingSchema).default([]) }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'RECORD_CREATE',
});

export const getMyPatientRecord = defineTool({
  name: 'getMyPatientRecord',
  description: 'Patient portal: the signed-in patient views their own banner and upcoming appointments.',
  input: z.object({}),
  output: z.object({ patient: PatientBannerSchema }),
  roles: ['PATIENT'], requiresPatientContext: false, write: false, exposeToLlm: false, audit: 'PATIENT_VIEW',
});

export const _everyone = EVERYONE;
